/**
 * Production Service - Handles all comprehensive production data operations in Firestore
 * Covers PregnancyChecks, ReturnToEstrus, Weanings, AnimalMovements, and Boar collections
 */

import { db } from "../../../firebase";

// ============================================================================
// WRITE OPERATIONS
// ============================================================================

/**
 * Generic helper to add a record to a collection
 * @param {string} userId - User ID
 * @param {string} collectionName - Collection name
 * @param {string} mac - Animal MAC address
 * @param {Object} data - Form data to save
 * @returns {Promise<string>} The created document ID
 */
async function addRecord(userId, collectionName, mac, data) {
	const timestamp = Date.now();
	const docId = timestamp.toString();

	// Filter out undefined values (Firestore doesn't accept undefined)
	const cleanData = Object.fromEntries(
		Object.entries(data).filter(([, value]) => value !== undefined)
	);

	const recordData = {
		...cleanData,
		MAC: mac,
		Uid: userId,
		t: timestamp,
		Archived: false,
	};

	await db
		.collection("Users")
		.doc(userId)
		.collection(collectionName)
		.doc(docId)
		.set(recordData);

	return docId;
}

/**
 * Adds a birth record for an animal
 * @param {string} userId - User ID
 * @param {string} mac - Animal MAC address
 * @param {Object} data - Birth form data
 * @returns {Promise<string>} The created document ID
 */
export async function addBirth(userId, mac, data) {
	return addRecord(userId, "Births", mac, data);
}

/**
 * Adds an insemination record for an animal
 * @param {string} userId - User ID
 * @param {string} mac - Animal MAC address
 * @param {Object} data - Insemination form data
 * @returns {Promise<string>} The created document ID
 */
export async function addInsemination(userId, mac, data) {
	return addRecord(userId, "Inseminations", mac, data);
}

/**
 * Adds a pregnancy check record for an animal
 * @param {string} userId - User ID
 * @param {string} mac - Animal MAC address
 * @param {Object} data - Pregnancy check form data
 * @returns {Promise<string>} The created document ID
 */
export async function addPregnancyCheck(userId, mac, data) {
	return addRecord(userId, "PregnancyChecks", mac, data);
}

/**
 * Adds a return to estrus record for an animal
 * @param {string} userId - User ID
 * @param {string} mac - Animal MAC address
 * @param {Object} data - Return to estrus form data
 * @returns {Promise<string>} The created document ID
 */
export async function addReturnToEstrus(userId, mac, data) {
	return addRecord(userId, "ReturnToEstrus", mac, data);
}

/**
 * Adds a weaning record for an animal
 * @param {string} userId - User ID
 * @param {string} mac - Animal MAC address
 * @param {Object} data - Weaning form data
 * @returns {Promise<string>} The created document ID
 */
export async function addWeaning(userId, mac, data) {
	return addRecord(userId, "Weanings", mac, data);
}

/**
 * Adds an animal movement record
 * @param {string} userId - User ID
 * @param {string} mac - Animal MAC address
 * @param {Object} data - Movement form data
 * @returns {Promise<string>} The created document ID
 */
export async function addAnimalMovement(userId, mac, data) {
	return addRecord(userId, "AnimalMovements", mac, data);
}

/**
 * Adds a general event record for an animal
 * @param {string} userId - User ID
 * @param {string} mac - Animal MAC address
 * @param {Object} data - General event form data
 * @returns {Promise<string>} The created document ID
 */
export async function addGeneralEvent(userId, mac, data) {
	return addRecord(userId, "GeneralEvents", mac, data);
}

/**
 * Unified function to save a production record based on type
 * @param {string} userId - User ID
 * @param {string} recordType - Type of record (birth, insemination, etc.)
 * @param {string} mac - Animal MAC address
 * @param {Object} data - Form data to save
 * @returns {Promise<string>} The created document ID
 */
export async function saveProductionRecord(userId, recordType, mac, data) {
	if (!userId || !mac) {
		throw new Error("userId and mac are required");
	}

	switch (recordType) {
		case "birth":
			return addBirth(userId, mac, data);
		case "insemination":
			return addInsemination(userId, mac, data);
		case "pregnancyCheck":
			return addPregnancyCheck(userId, mac, data);
		case "returnToEstrus":
			return addReturnToEstrus(userId, mac, data);
		case "weaning":
			return addWeaning(userId, mac, data);
		case "animalMovement":
			return addAnimalMovement(userId, mac, data);
		case "generalEvent":
			return addGeneralEvent(userId, mac, data);
		default:
			throw new Error(`Unknown record type: ${recordType}`);
	}
}

// ============================================================================
// READ OPERATIONS
// ============================================================================

/**
 * Generic helper to fetch records from a collection for a specific animal
 * @param {string} userId - User ID
 * @param {string} collectionName - Collection name (e.g., "PregnancyChecks")
 * @param {string} mac - Animal MAC address
 * @param {number} limit - Max records to return
 * @returns {Promise<Array>} Array of records
 */
async function getRecords(userId, collectionName, mac, limit = 20) {
	try {
		const snapshot = await db
			.collection("Users")
			.doc(userId)
			.collection(collectionName)
			.where("MAC", "==", mac)
			.where("Archived", "==", false)
			.get();

		const records = snapshot.docs
			.map(doc => {
				const data = doc.data();
				return { 
					...data, 
					id: doc.id, 
					t: data.t || parseInt(doc.id) 
				};
			})
			.sort((a, b) => (b.t || 0) - (a.t || 0))
			.slice(0, limit);

		return records;
	} catch (error) {
		// Graceful fallback: if composite index is missing, query by MAC only and filter client-side
		if (error?.code === "failed-precondition" || error?.message?.includes("index")) {
			console.warn(`[productionService][getRecords] - Missing index for ${collectionName}, falling back to client-side filter`);
			try {
				const fallbackSnapshot = await db
					.collection("Users")
					.doc(userId)
					.collection(collectionName)
					.where("MAC", "==", mac)
					.get();

				return fallbackSnapshot.docs
					.map(doc => {
						const data = doc.data();
						return { ...data, id: doc.id, t: data.t || parseInt(doc.id) };
					})
					.filter(r => r.Archived !== true)
					.sort((a, b) => (b.t || 0) - (a.t || 0))
					.slice(0, limit);
			} catch (fallbackError) {
				console.error(`[productionService][getRecords] - Fallback also failed for ${collectionName}:`, fallbackError);
				return [];
			}
		}
		console.error(`[productionService][getRecords] - Error fetching ${collectionName}:`, error);
		return [];
	}
}

/**
 * Gets pregnancy check records for an animal
 * @param {string} userId - User ID
 * @param {string} mac - Animal MAC address
 * @param {number} limit - Limit
 */
export async function getPregnancyChecks(userId, mac, limit) {
	return getRecords(userId, "PregnancyChecks", mac, limit);
}

/**
 * Gets return to estrus records for an animal
 * @param {string} userId - User ID
 * @param {string} mac - Animal MAC address
 * @param {number} limit - Limit
 */
export async function getReturnToEstrus(userId, mac, limit) {
	return getRecords(userId, "ReturnToEstrus", mac, limit);
}

/**
 * Gets weaning records for an animal
 * @param {string} userId - User ID
 * @param {string} mac - Animal MAC address
 * @param {number} limit - Limit
 */
export async function getWeanings(userId, mac, limit) {
	return getRecords(userId, "Weanings", mac, limit);
}

/**
 * Gets animal movement records for an animal
 * @param {string} userId - User ID
 * @param {string} mac - Animal MAC address
 * @param {number} limit - Limit
 */
export async function getAnimalMovements(userId, mac, limit) {
	return getRecords(userId, "AnimalMovements", mac, limit);
}

/**
 * Gets boar arrival records
 * @param {string} userId - User ID
 * @param {string} mac - Boar MAC address
 * @param {number} limit - Limit
 */
export async function getBoarArrivals(userId, mac, limit) {
	return getRecords(userId, "BoarArrivals", mac, limit);
}

/**
 * Gets boar training records
 * @param {string} userId - User ID
 * @param {string} mac - Boar MAC address
 * @param {number} limit - Limit
 */
export async function getBoarTraining(userId, mac, limit) {
	return getRecords(userId, "BoarTraining", mac, limit);
}

/**
 * Gets boar collection records
 * @param {string} userId - User ID
 * @param {string} mac - Boar MAC address
 * @param {number} limit - Limit
 */
export async function getBoarCollections(userId, mac, limit) {
	return getRecords(userId, "BoarCollections", mac, limit);
}

/**
 * Gets boar QC records
 * @param {string} userId - User ID
 * @param {string} mac - Boar MAC address
 * @param {number} limit - Limit
 */
export async function getBoarQC(userId, mac, limit) {
	return getRecords(userId, "BoarQC", mac, limit);
}

/**
 * Gets boar natural service records
 * @param {string} userId - User ID
 * @param {string} mac - Boar MAC address
 * @param {number} limit - Limit
 */
export async function getBoarNaturalServices(userId, mac, limit) {
	return getRecords(userId, "BoarNaturalServices", mac, limit);
}

/**
 * Gets boar health event records
 * @param {string} userId - User ID
 * @param {string} mac - Boar MAC address
 * @param {number} limit - Limit
 */
export async function getBoarHealthEvents(userId, mac, limit) {
	return getRecords(userId, "BoarHealthEvents", mac, limit);
}

/**
 * Unified accessor for production records based on type
 * @param {string} userId - User ID
 * @param {string} recordType - Type of record to fetch
 * @param {string} mac - Animal MAC address
 * @param {number} limit - Optional limit (default 20)
 * @returns {Promise<Array>} Array of records
 */
export async function getProductionRecords(userId, recordType, mac, limit = 20) {
	if (!userId || !mac) return [];

	switch (recordType) {
		case "pregnancy-check":
			return getPregnancyChecks(userId, mac, limit);
		case "return-to-estrus":
			return getReturnToEstrus(userId, mac, limit);
		case "weaning":
			return getWeanings(userId, mac, limit);
		case "movement":
			return getAnimalMovements(userId, mac, limit);
		case "boar-arrival":
			return getBoarArrivals(userId, mac, limit);
		case "boar-training":
			return getBoarTraining(userId, mac, limit);
		case "boar-collection":
			return getBoarCollections(userId, mac, limit);
		case "boar-qc":
			return getBoarQC(userId, mac, limit);
		case "boar-natural-service":
			return getBoarNaturalServices(userId, mac, limit);
		case "boar-health":
			return getBoarHealthEvents(userId, mac, limit);
		default:
			console.warn(`[productionService] Unknown record type: ${recordType}`);
			return [];
	}
}


